import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { toPng } from 'html-to-image';
import { Link, useNavigate } from 'react-router-dom';
import type { BaziChart, CalculateChartReq } from '@guanwei/shared';
import { BirthForm } from '@/components/chart/BirthForm';
import { BaziChartCard } from '@/components/chart/BaziChartCard';
import { ReportPanel } from '@/components/chart/ReportPanel';
import { CalibrationFlow } from '@/components/calibration/CalibrationFlow';
import {
  PremiumReportRenderer,
  ReportPaper,
  parseReportSegments,
} from '@/components/premium/ReportPaper';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useAuthStore } from '@/lib/auth.store';
import {
  createChart,
  getChartDetail,
  listMyCharts,
  type ChartHistoryItem,
  type ChartLatestReports,
} from '@/lib/chart.api';

type Step = 'form' | 'chart';

const PENDING_CHART_REQ_KEY = 'guanwei.pendingChartReq';
const IMMERSIVE_MAIN_CLASS =
  'mobile-immersive-page h-full min-h-0 overflow-hidden px-3 py-3 sm:px-6 lg:px-8 lg:py-6';
const chartShellClass = (historyCollapsed: boolean) =>
  `mobile-immersive-shell relative mx-auto grid h-full min-h-0 w-full max-w-[1680px] overflow-hidden rounded-2xl border border-border bg-bg-1 shadow-gold lg:rounded-3xl ${
    historyCollapsed
      ? 'grid-cols-[minmax(0,1fr)] lg:grid-cols-[0_minmax(0,1fr)]'
      : 'grid-cols-[minmax(0,1fr)] lg:grid-cols-[380px_minmax(0,1fr)]'
  }`;
const chartHistoryPanelClass = (historyCollapsed: boolean) =>
  `mobile-immersive-history absolute inset-y-0 left-0 z-30 h-full w-[min(82vw,360px)] min-h-0 overflow-y-auto border-r border-border bg-bg p-4 shadow-gold transition-transform duration-base lg:relative lg:z-auto lg:w-auto lg:shadow-none lg:p-8 ${
    historyCollapsed
      ? '-translate-x-full lg:invisible lg:p-0 lg:opacity-0'
      : 'translate-x-0 lg:visible lg:opacity-100'
  }`;
const CHART_CONTENT_PANEL_CLASS =
  'mobile-immersive-content relative order-2 h-full min-h-0 overflow-y-auto bg-bg-1 px-4 py-6 sm:px-8 lg:px-12 lg:py-10 xl:px-16';

function HistoryToggle({ collapsed, onClick }: { collapsed: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute left-3 top-3 z-40 inline-flex items-center gap-2 rounded-full border border-border-accent bg-accent px-4 py-2 text-sm font-medium text-accent-fg shadow-gold transition-all hover:bg-accent-hover active:bg-accent-active lg:left-4 lg:top-4"
      aria-label={collapsed ? '查看历史记录' : '隐藏历史记录'}
    >
      <span aria-hidden="true">☰</span>
      <span>{collapsed ? '查看历史' : '隐藏历史'}</span>
    </button>
  );
}

function formatHistoryMeta(item: ChartHistoryItem) {
  const reports = Object.values(item.latestReports ?? {}).filter(Boolean).length;
  return `${item.baziKey}${reports > 0 ? ` · 已存${reports}份报告` : ''}`;
}

export function ChartPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  const [step, setStep] = useState<Step>('form');
  const [bazi, setBazi] = useState<BaziChart | null>(null);
  const [chartId, setChartId] = useState<string | null>(null);
  const [chartName, setChartName] = useState<string | null>(null);
  const [latestReports, setLatestReports] = useState<ChartLatestReports>({});
  const [history, setHistory] = useState<ChartHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loadingHistoryId, setLoadingHistoryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [showFullFlow, setShowFullFlow] = useState(false);
  const [historyCollapsed, setHistoryCollapsed] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth < 1024,
  );
  const [pendingAfterLogin, setPendingAfterLogin] = useState(false);
  const pendingRestoredRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const savedFullReportContent = latestReports.full?.content?.trim() ?? '';
  const shellClass = useMemo(() => chartShellClass(historyCollapsed), [historyCollapsed]);
  const historyClass = useMemo(() => chartHistoryPanelClass(historyCollapsed), [historyCollapsed]);

  const showChartResult = (result: {
    bazi: BaziChart;
    chartId: string | null;
    displayName: string | null;
    latestReports?: ChartLatestReports;
  }) => {
    setBazi(result.bazi);
    setChartId(result.chartId);
    setChartName(result.displayName);
    setLatestReports(result.latestReports ?? {});
    setStep('chart');
  };

  const refreshHistory = async () => {
    if (!user) {
      setHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      setHistory(await listMyCharts());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    void refreshHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!initialized || !user || pendingRestoredRef.current) return;
    const raw = window.sessionStorage.getItem(PENDING_CHART_REQ_KEY);
    if (!raw) return;

    pendingRestoredRef.current = true;
    setError(null);
    setLoading(true);
    setPendingAfterLogin(true);
    setShowFullFlow(false);
    setStep('chart');

    void (async () => {
      try {
        const req = JSON.parse(raw) as CalculateChartReq;
        const result = await createChart(req);
        window.sessionStorage.removeItem(PENDING_CHART_REQ_KEY);
        showChartResult({
          bazi: result.bazi,
          chartId: result.chartId,
          displayName: result.displayName,
          latestReports: result.latestReports,
        });
        await refreshHistory();
      } catch (err) {
        pendingRestoredRef.current = false;
        setError((err as Error).message);
      } finally {
        setLoading(false);
        setPendingAfterLogin(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, user?.id]);

  const handleSubmit = async (req: CalculateChartReq) => {
    setError(null);
    setLoading(true);
    setShowFullFlow(false);
    try {
      if (!user) {
        window.sessionStorage.setItem(PENDING_CHART_REQ_KEY, JSON.stringify(req));
        navigate('/auth/login', { state: { from: '/chart' } });
        return;
      }

      const result = await createChart(req);
      showChartResult({
        bazi: result.bazi,
        chartId: result.chartId,
        displayName: result.displayName,
        latestReports: result.latestReports,
      });
      await refreshHistory();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const openHistory = async (id: string) => {
    setError(null);
    setLoadingHistoryId(id);
    setShowFullFlow(false);
    try {
      const detail = await getChartDetail(id);
      setChartId(detail.id);
      setChartName(detail.displayName);
      setBazi(detail.bazi);
      setLatestReports(detail.latestReports ?? {});
      setStep('chart');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoadingHistoryId(null);
    }
  };

  const handleBriefReportDone = async (reportId?: string, content?: string) => {
    if (!chartId || !content) return;
    setLatestReports((prev) => ({
      ...prev,
      brief: {
        id: reportId ?? prev.brief?.id ?? 'latest',
        stage: 'brief',
        status: 'completed',
        content,
        modelCode: prev.brief?.modelCode ?? '',
        promptVersion: prev.brief?.promptVersion ?? 0,
        cacheKey: prev.brief?.cacheKey ?? '',
        createdAt: new Date().toISOString(),
      },
    }));
    await refreshHistory();
  };

  const handleFullReportDone = async (reportId?: string, content?: string) => {
    if (!chartId || !content) return;
    setLatestReports((prev) => ({
      ...prev,
      full: {
        id: reportId ?? prev.full?.id ?? 'latest-full',
        stage: 'full',
        status: 'completed',
        content,
        modelCode: prev.full?.modelCode ?? '',
        promptVersion: prev.full?.promptVersion ?? 0,
        cacheKey: prev.full?.cacheKey ?? '',
        createdAt: new Date().toISOString(),
      },
    }));
    await refreshHistory();
  };

  const handleExport = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor:
          getComputedStyle(document.documentElement).getPropertyValue('--color-bg-1').trim() ||
          undefined,
      });
      const link = document.createElement('a');
      link.download = `观微-${chartName ?? bazi?.baziKey ?? 'chart'}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      setError(`保存失败：${(err as Error).message}`);
    } finally {
      setExporting(false);
    }
  };

  const historyPanel = (className?: string) => (
    <aside className={className}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-accent">Archive</p>
          <p className="mt-2 font-serif text-2xl text-fg">历史命盘</p>
          <p className="mt-2 text-xs leading-5 text-fg-muted">
            同一出生时间只保留最新记录，报告也只保留最新版本。
          </p>
        </div>
        {user && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => void refreshHistory()}
            disabled={historyLoading}
            className="shrink-0 whitespace-nowrap"
          >
            {historyLoading ? '刷新中' : '刷新'}
          </Button>
        )}
      </div>

      {!user ? (
        <div className="rounded-2xl border border-dashed border-border bg-bg-2 p-5 text-sm leading-7 text-fg-muted">
          <p>登录之后显示历史命盘。</p>
          <Link to="/auth/login" className="mt-3 inline-flex text-accent hover:text-accent-hover">
            去登录
          </Link>
        </div>
      ) : history.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-bg-2 p-5 text-sm leading-7 text-fg-muted">
          暂无历史命盘。完成一次排盘后会自动保存到这里。
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((item) => {
            const active = item.id === chartId;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => void openHistory(item.id)}
                className={
                  'group w-full rounded-2xl border p-4 text-left transition-all duration-fast ' +
                  (active
                    ? 'border-accent bg-accent-soft text-fg shadow-gold'
                    : 'border-border bg-bg-1 text-fg-1 hover:-translate-y-0.5 hover:border-accent hover:bg-bg-2 hover:shadow-md')
                }
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-serif text-base text-fg">{item.displayName}</p>
                    <p className="mt-1 text-xs text-fg-muted">{formatHistoryMeta(item)}</p>
                  </div>
                  <span className="rounded-full border border-border px-2 py-1 text-[11px] text-fg-muted group-hover:border-accent group-hover:text-accent">
                    {loadingHistoryId === item.id ? '读取中' : active ? '当前' : '查看'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </aside>
  );

  if (step === 'form') {
    return (
      <main className={IMMERSIVE_MAIN_CLASS}>
        <div className={shellClass}>
          {historyPanel(historyClass)}
          <div className={CHART_CONTENT_PANEL_CLASS}>
            <HistoryToggle
              collapsed={historyCollapsed}
              onClick={() => setHistoryCollapsed((value) => !value)}
            />
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <p className="text-xs uppercase tracking-[0.28em] text-accent">Birth Chart</p>
              <h1 className="mt-3 font-serif text-4xl text-fg">填写出生信息</h1>
              <p className="mt-3 mb-8 max-w-2xl text-sm leading-7 text-fg-muted">
                所有信息均加密存储，仅你本人可见。完成后将生成你的八字命盘，并可继续进入报告解读。
              </p>
            </motion.div>

            <Card className="rounded-2xl p-6 sm:p-8 xl:p-10">
              <BirthForm onSubmit={handleSubmit} loading={loading} />
              {error && <p className="mt-4 text-center text-sm text-danger">{error}</p>}
            </Card>

            {!user && (
              <p className="mt-6 text-center text-xs text-fg-muted">
                点击立即排盘后需先
                <Link to="/auth/login" className="text-accent hover:text-accent-hover">
                  登录
                </Link>{' '}
                ，登录完成会自动展示排盘结果
              </p>
            )}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={IMMERSIVE_MAIN_CLASS}>
      <div className={shellClass}>
        {historyPanel(historyClass)}
        <div className={`${CHART_CONTENT_PANEL_CLASS} space-y-8`}>
          <HistoryToggle
            collapsed={historyCollapsed}
            onClick={() => setHistoryCollapsed((value) => !value)}
          />
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-fg-muted">当前命盘</p>
              <h1 className="mt-1 font-serif text-2xl text-fg">
                {chartName ?? bazi?.baziKey ?? '新命盘'}
              </h1>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setStep('form')}>
                新排盘
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
                {exporting ? '导出中…' : '保存为图片'}
              </Button>
            </div>
          </div>

          {error && <p className="text-sm text-danger">{error}</p>}

          {pendingAfterLogin && (
            <Card className="p-6 text-center text-sm text-fg-2">正在为你生成刚才提交的命盘…</Card>
          )}

          {bazi && (
            <BaziChartCard
              ref={cardRef}
              bazi={bazi}
              showWatermark
              ownerName={user?.name ?? undefined}
            />
          )}

          {user && chartId && bazi && (
            <ReportPanel
              chartId={chartId}
              initialContent={latestReports.brief?.content}
              onReportDone={handleBriefReportDone}
            />
          )}

          {user && chartId && bazi && savedFullReportContent && !showFullFlow && (
            <ReportPaper
              title="完整 7 段战略报告"
              subtitle="已保存的完整解读，可随时从历史命盘中继续查看。"
              progress={100}
              action={
                <Button size="sm" variant="outline" onClick={() => setShowFullFlow(true)}>
                  重新生成
                </Button>
              }
            >
              <PremiumReportRenderer
                segments={parseReportSegments(savedFullReportContent)}
                streaming={false}
              />
            </ReportPaper>
          )}

          {user && chartId && bazi && !savedFullReportContent && !showFullFlow && (
            <Card variant="gold" className="p-8 text-center">
              <p className="mb-2 font-serif text-xl text-fg">想要更深的战略级解读？</p>
              <p className="mb-6 text-sm text-fg-2">
                进入定盘对话，由观微先生为你筛选判定流派，生成完整 7 段报告 （财富 / 事业 / 感情 /
                健康 / 流年 / 行动清单）
              </p>
              <Button size="lg" onClick={() => setShowFullFlow(true)}>
                开始完整解读
              </Button>
            </Card>
          )}

          {user && chartId && showFullFlow && (
            <CalibrationFlow
              chartId={chartId}
              onReportStart={() => void refreshHistory()}
              onReportDone={handleFullReportDone}
            />
          )}

          {!user && (
            <Card className="p-8 text-center">
              <p className="mb-2 font-serif text-xl text-fg">查看深度报告</p>
              <p className="mb-6 text-sm text-fg-2">注册账号后即可获取「观微先生」的简版命理分析</p>
              <Link to="/auth/register">
                <Button size="lg">免费注册</Button>
              </Link>
            </Card>
          )}
        </div>
      </div>
    </main>
  );
}
